// Block gallery — top-level read-only listing of all 14 registered Blocks
// grouped into 4 sections (HEADER / CONTENT / FOOTER / META) per Banks's
// P-037.25 D-133 audit. Each Block links to the per-Block preview screen.
//
// **Section grouping (P-037.25 D-133).** The category enum already exists
// (`BlockCategory` in src/types.ts; same 4-value set drives the
// LibraryScreen + BlockPickerModal); the gallery just adopts the same
// 4-section grouped layout instead of a flat list. Within-section
// ordering follows the registry's insertion order (src/registry/blocks.ts).
//
// **Scope vs `<LibraryScreen />`.** Library lists Block schemas (read-only
// reference for the form generator). The gallery lists Blocks for VISUAL
// preview — different intent. Both stay in V1; consider merging at a
// future polish pass if the schemas-vs-rendering distinction blurs.

import { blocks } from '../../registry/blocks'
import type { BlockCategory } from '../../types'

const CATEGORY_ORDER: readonly BlockCategory[] = ['header', 'content', 'footer', 'meta']

const ENTRIES = Object.values(blocks)

interface CategoryGroup {
  readonly category: BlockCategory
  readonly items: readonly (typeof ENTRIES)[number][]
}

const GROUPED: readonly CategoryGroup[] = CATEGORY_ORDER.map((category) => ({
  category,
  items: ENTRIES.filter((b) => b.category === category),
}))

export function BlockGalleryScreen() {
  return (
    <div class="space-y-8">
      <header>
        <h2 class="m-0 text-lg font-semibold">Block gallery</h2>
        <p class="m-0 mt-2 text-sm text-gray-600">
          Visual preview for each of the {ENTRIES.length} registered Blocks, grouped into 4
          sections. Click into a Block to see it rendered in isolation — toggle viewport
          (desktop/mobile) and Block-level options/inputs to verify the rendered output matches
          the design intent.
        </p>
      </header>

      {GROUPED.map(({ category, items }) => (
        <section key={category} aria-labelledby={`gallery-cat-${category}`}>
          <h3
            id={`gallery-cat-${category}`}
            class="m-0 mb-3 text-sm font-semibold uppercase tracking-wide text-gray-600"
          >
            {category}
          </h3>
          <ul class="m-0 grid list-none grid-cols-1 gap-3 p-0 md:grid-cols-2">
            {items.map((b) => (
              <li key={b.id}>
                <a
                  href={`#/blocks/${b.id}`}
                  class="flex items-baseline justify-between gap-3 rounded border border-gray-200 p-4 hover:bg-gray-50"
                >
                  <div class="min-w-0">
                    <div class="text-base font-semibold text-gray-900">{b.label}</div>
                    <code class="text-xs text-gray-500">{b.id}</code>
                  </div>
                  <span class="shrink-0 text-sm font-medium text-blue-700">Preview →</span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
